/**
 * Select position action popover for draw assignments.
 * Provides actions for assigning, withdrawing, seeding, swapping participants.
 */
import { participantProfileModal } from 'components/modals/participantProfileModal';
import { additionalSeedAction } from 'components/popovers/additionalSeedAction';
import { promptByeScheduling } from 'components/popovers/byeSchedulingPrompt';
import { navigateToEvent } from 'components/tables/common/navigateToEvent';
import { isSchedulingAmbiguity } from 'functions/isSchedulingAmbiguity';
import { selectParticipant } from 'components/modals/selectParticipant';
import { mutationRequest } from 'services/mutation/mutationRequest';
import { tipster } from 'components/popovers/tipster';
import { validators } from 'courthive-components';
import { tools } from 'tods-competition-factory';
import { t } from 'i18n';

const actionLabels: Record<string, string> = {
  ADDITIONAL_SEED: 'Seed as additional',
  ALTERNATE: 'Assign alternate',
  ASSIGN: 'Assign participant',
  BYE: 'Assign BYE',
  NICKNAME: 'Set nickname',
  REMOVE_PARTICIPANT: 'Remove assignment',
  REMOVE: 'Remove assignment',
  QUALIFIER: 'Assign qualifier',
  LUCKY: 'Assign Lucky Loser',
  SEED_CASCADE: 'Withdraw seed (cascade)',
  SEED_VALUE: 'Assign seed',
  SWAP: 'Swap draw positions',
  VIEW_PLAYER_CARD: 'View player card',
  WITHDRAW: 'Withdraw participant',
};

export function selectPositionAction({
  pointerEvent,
  actions,
  callback,
  title,
}: {
  pointerEvent: PointerEvent;
  actions: any[];
  callback: () => void;
  /** Displayed above the actions — used to say WHY a seed exists where the basis is not the usual one. */
  title?: string;
}): void {
  const target = pointerEvent.target as HTMLElement;
  const handleClick = (action: any) => {
    if (['WITHDRAW', 'BYE', 'REMOVE', 'REMOVE_PARTICIPANT'].includes(action.type)) noChoiceAction({ action, callback });
    if (['ASSIGN', 'ALTERNATE', 'SWAP', 'QUALIFIER', 'LUCKY'].includes(action.type))
      assignParticipant({ action, callback });
    if (['SEED_CASCADE'].includes(action.type)) seedCascadeAction({ action, callback });
    if (['SEED_VALUE'].includes(action.type)) assignSeed({ target, action, callback });
    if (['ADDITIONAL_SEED'].includes(action.type)) additionalSeedAction({ target, action, callback });
    if (['NICKNAME'].includes(action.type)) assignNickname({ target, action, callback });
    if (['VIEW_PLAYER_CARD'].includes(action.type)) viewPlayerCard({ action });
    if (action.type === 'FOLLOW_TO_STRUCTURE') followToStructure({ action });
  };
  const options = actions
    ?.filter(({ type }) => actionLabels[type] || type === 'FOLLOW_TO_STRUCTURE')
    .map((action) => ({
      option: action.type === 'FOLLOW_TO_STRUCTURE' ? action.label : actionLabels[action.type] || action.type,
      onClick: () => handleClick(action),
    }));

  if (options?.length) tipster({ options, target, title, config: { arrow: false, offset: [0, 0] } });
}

function noChoiceAction({ action, callback }: { action: any; callback: () => void }) {
  const dispatch = (params: any) => {
    const methods = [{ method: action.method, params }];
    mutationRequest({ methods, callback: postMutation });
  };

  // Assigning a BYE to an already-scheduled matchUp is the one "no choice" action
  // that does have a choice in it: keep the court slot (the director is mid-swap) or
  // give it back. The engine refuses to guess and returns ERR_MATCHUP_HAS_SCHEDULING;
  // we ask and re-dispatch with the answer. Cancelling assigns no BYE.
  const postMutation = (result: any) => {
    if (result.success) return callback();
    if (isSchedulingAmbiguity(result)) {
      promptByeScheduling({ onChoice: (preserveScheduling) => dispatch({ ...action.payload, preserveScheduling }) });
      return undefined;
    }
    return console.log({ result });
  };

  dispatch(action.payload);
}

function seedCascadeAction({ action, callback }: { action: any; callback: () => void }) {
  const postMutation = (result: any) => {
    if (!result.success) return console.log({ result });

    const vacatedDrawPosition = result.results?.[0]?.vacatedDrawPosition;
    callback();

    if (vacatedDrawPosition) {
      requestAnimationFrame(() => highlightDrawPosition(vacatedDrawPosition));
    }
  };
  const methods = [{ method: action.method, params: action.payload }];
  mutationRequest({ methods, callback: postMutation });
}

export function highlightDrawPosition(drawPosition: number) {
  const el = document.querySelector(`.tmx-p[data-draw-position="${drawPosition}"]`) as HTMLElement;
  if (!el) return;

  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.add('draw-position-highlight');
  setTimeout(() => el.classList.remove('draw-position-highlight'), 4000);
}

function viewPlayerCard({ action }: { action: any }) {
  const participantId = action.payload?.participantId;
  if (!participantId) return;
  participantProfileModal({ participantId, readOnly: true });
}

function followToStructure({ action }: { action: any }) {
  const { eventId, drawId, structureId, matchUpId } = action.payload ?? {};
  if (!drawId || !structureId) return;
  // navigateToEvent stashes matchUpId as a pending focus, so the target
  // structure renders and the participant's matchUp is scrolled-to + pulsed.
  navigateToEvent({ eventId, drawId, structureId, matchUpId, renderDraw: true });
}

function assignParticipant({ action, callback }: { action: any; callback: () => void }) {
  const onSelection = (params: any) => {
    const postMutation = (result: any) => (result.success ? callback() : console.log({ result }));
    const methods = [
      {
        params: { ...action.payload, ...params },
        method: action.method,
      },
    ];
    mutationRequest({ methods, callback: postMutation });
  };

  (selectParticipant as any)({ action, onSelection, selectOnEnter: true });
}

function assignSeed({ target, action, callback }: { target: HTMLElement; action: any; callback: () => void }) {
  let tip: any;

  function onKeyDown(e: KeyboardEvent) {
    if (e?.key === 'Enter') {
      const seedValue = (e.target as HTMLInputElement).value;
      if (tools.isConvertableInteger(seedValue)) {
        const postMutation = (result: any) => (result.success ? callback() : console.log({ result }));
        const methods = [
          {
            params: { ...action.payload, seedValue },
            method: action.method,
          },
        ];
        mutationRequest({ methods, callback: postMutation });
      }
      tip.destroy();
    }
  }
  const menuItems = [
    {
      label: t('matchUpActions.assignSeedNumber'),
      validator: validators.numericValidator,
      placeholder: t('matchUpActions.seedNumber'),
      field: 'seedNumber',
      id: 'seedNumber',
      type: 'input',
      focus: true,
      onKeyDown,
    },
  ];

  tip = tipster({ menuItems, target, config: { arrow: false, offset: [0, 0] } });
}

function assignNickname({ target, action, callback }: { target: HTMLElement; action: any; callback: () => void }) {
  let tip: any;

  function onKeyDown(e: KeyboardEvent) {
    if (e?.key === 'Enter') {
      const participantOtherName = (e.target as HTMLInputElement).value || undefined;
      const postMutation = (result: any) => (result.success ? callback() : console.log({ result }));
      const methods = [
        {
          params: { ...action.payload, participantOtherName },
          method: action.method,
        },
      ];
      mutationRequest({ methods, callback: postMutation });
      tip.destroy();
    }
  }
  const menuItems = [
    {
      label: t('matchUpActions.nickname'),
      placeholder: action.participant?.participantOtherName || 'Nickname',
      value: action.participant?.participantOtherName || '',
      field: 'nickname',
      id: 'nickname',
      type: 'input',
      focus: true,
      onKeyDown,
    },
  ];

  tip = tipster({ menuItems, target, config: { arrow: false, offset: [0, 0] } });
}
